import { useState } from 'react';
import { Button, Stack, Table, FileInput, Group, Alert } from '@mantine/core';
import { IconUpload, IconTrash } from '@tabler/icons-react';
import { type CompanyAppendix } from 'src/company/types';
import { useCompanyAppendices } from 'src/company/hooks/useCompanyAppendices';
import { useCreateCompanyAppendix } from 'src/company/hooks/useCreateCompanyAppendix';
import { useDeleteCompanyAppendix } from 'src/company/hooks/useDeleteCompanyAppendix';
import { ExampleStack, type Example } from '../';

function apiHooksDemo(): Example {
  return {
    description: 'Upload, list, and delete company appendices using the API hooks',
    render: <CompanyAppendicesDemo />,
  };
}

function rawJsonDemo(): Example {
  return {
    description: 'Raw JSON response from useCompanyAppendices hook',
    render: <RawJsonDemo />,
  };
}

export default function CompanyAppendicesSandbox() {
  return <ExampleStack examples={[apiHooksDemo(), rawJsonDemo()]} />;
}

function CompanyAppendicesDemo() {
  const [file, setFile] = useState<File | null>(null);
  const { appendices, isLoading, refetch, error } = useCompanyAppendices();
  const { createCompanyAppendix, isLoading: isCreating } = useCreateCompanyAppendix();
  const { deleteCompanyAppendix, isLoading: isDeleting } = useDeleteCompanyAppendix();

  const handleUpload = async () => {
    if (!file) return;
    await createCompanyAppendix(file);
    setFile(null);
    refetch();
  };

  const handleDelete = async (id: string) => {
    await deleteCompanyAppendix(id);
    refetch();
  };

  if (error) {
    return <Alert color="red">Error loading appendices: {String(error)}</Alert>;
  }

  return (
    <Stack>
      <Group>
        <FileInput
          placeholder="Select file"
          value={file}
          onChange={setFile}
          leftSection={<IconUpload size={16} />}
        />
        <Button onClick={handleUpload} loading={isCreating} disabled={!file}>
          Upload
        </Button>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>File name</Table.Th>
            <Table.Th>File ID</Table.Th>
            <Table.Th>Size</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <AppendixRows
            appendices={appendices}
            isLoading={isLoading}
            isDeleting={isDeleting}
            handleDelete={handleDelete}
          />
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function RawJsonDemo() {
  const { appendices, isLoading, error } = useCompanyAppendices();

  if (isLoading) return <pre>Loading...</pre>;
  if (error) return <pre>Error: {String(error)}</pre>;

  return <pre style={{ margin: 0 }}>{JSON.stringify(appendices, null, 2)}</pre>;
}

type Props = {
  appendices: CompanyAppendix[];
  isLoading: boolean;
  isDeleting: boolean;
  handleDelete: (id: string) => Promise<void>;
};

function AppendixRows({ appendices, isLoading, isDeleting, handleDelete }: Props) {
  if (isLoading) {
    return (
      <Table.Tr>
        <Table.Td colSpan={7}>Loading...</Table.Td>
      </Table.Tr>
    );
  }

  if (appendices.length === 0) {
    return (
      <Table.Tr>
        <Table.Td colSpan={7}>No appendices</Table.Td>
      </Table.Tr>
    );
  }

  return appendices.map(appendix => (
    <Table.Tr key={appendix.id}>
      <Table.Td>{appendix.id}</Table.Td>
      <Table.Td>{appendix.file.fileName}</Table.Td>
      <Table.Td>{appendix.file.id}</Table.Td>
      <Table.Td>{appendix.file.fileSize}</Table.Td>
      <Table.Td>{appendix.file.fileType}</Table.Td>
      <Table.Td>{appendix.createdAt}</Table.Td>
      <Table.Td>
        <Button
          variant="subtle"
          color="red"
          size="xs"
          leftSection={<IconTrash size={14} />}
          onClick={() => handleDelete(appendix.id)}
          loading={isDeleting}
        >
          Delete
        </Button>
      </Table.Td>
    </Table.Tr>
  ));
}
